/**
 * User Recommend Render (V1)
 * - DOM operations only
 * - Modal HTML is injected via innerHTML
 */

import { Roles } from "../../constants/roles";
import { Actions } from "../../constants/actions";
import { ModalSearchBase } from "../../components/modal-search/modal-search-base-handler";
import { ModalSearchListener } from "../../components/modal-search/modal-search-listener";
import { translator } from "../../translation/translator";
import commonUtil from "../../util/common-utils";
import { SpinnerRender } from "../../util/spinner/spinner-render";
import { RecommendBadgeIconsRender } from "../../recommend/recommend-badge-icons-render";
import { RecommendBadgeIconsUtils } from "../../recommend/recommend-badge-icons-utils";
import { userRecommendForm } from "./user-recommend-form";

interface RecommendButtonViewModel {
    rank: number;
    disabled: boolean;
    isCurrent: boolean;
}

interface RecommendViewModel {
    buttons: RecommendButtonViewModel[];
    canUnset: boolean;
}

interface ModalSearchBaseLike {
    setSelectedIds(ids: string[]): void;
    render(): void;
    hasSelectedId(id: string): boolean;
    toggleSelectedId(id: string): void;
    setSelectionMode(mode: string): void;
    setCloseOnSelect(shouldClose: boolean): void;
    open(): void;
    close(): void;
}

export class UserRecommendRender {
    public base: ModalSearchBaseLike | null;

    public viewModel: RecommendViewModel | null;

    public onPickRank: ((rank: number) => Promise<void> | void) | null;

    public onUnset: (() => Promise<void> | void) | null;

    public onClearAll: (() => Promise<void> | void) | null;

    public constructor() {
        this.base = this.createBaseSlot();
        this.viewModel = this.createViewModelSlot();
        this.onPickRank = this.createOnPickRankSlot();
        this.onUnset = this.createOnUnsetSlot();
        this.onClearAll = null;
    }

    public createBaseSlot(): ModalSearchBaseLike | null {
        return null;
    }

    public createViewModelSlot(): RecommendViewModel | null {
        return null;
    }

    public createOnPickRankSlot(): ((rank: number) => Promise<void> | void) | null {
        return null;
    }

    public createOnUnsetSlot(): (() => Promise<void> | void) | null {
        return null;
    }

    public getOverlay(): HTMLElement | null {
        return userRecommendForm.getOverlay(document);
    }

    public getModal(): HTMLElement | null {
        return userRecommendForm.getModal(document);
    }

    public getListRoot(): HTMLElement | null {
        return userRecommendForm.getListRoot(document);
    }

    public renderLoading(): void {
        this.ensureOverlay();
        this.viewModel = null;

        if (this.base) {
            this.base.setSelectedIds([]);
            this.base.render();
        }
    }

    public setActions(actions: {
        onPickRank?: ((rank: number) => Promise<void> | void) | null;
        onUnset?: (() => Promise<void> | void) | null;
        onClearAll?: (() => Promise<void> | void) | null;
    }): void {
        this.onPickRank = typeof actions?.onPickRank === "function" ? actions.onPickRank : null;
        this.onUnset = typeof actions?.onUnset === "function" ? actions.onUnset : null;
        this.onClearAll = typeof actions?.onClearAll === "function" ? actions.onClearAll : null;
    }

    public ensureOverlay(): void {
        if (this.getOverlay()) return;

        const t = (key: string, fallback: string): string => {
            const value = translator?.getTranslation(key);
            return value && value !== key ? value : fallback;
        };

        const html = `
            <div data-role="${Roles.USER_RECOMMEND_MODAL_OVERLAY}" class="product-recommend-modal-overlay" style="display:none">
                <div data-role="${Roles.USER_RECOMMEND_MODAL}" class="product-recommend-modal" style="display:none">
                    <div class="modal-header element-modal-header">
                        <div class="section-title-sub modal-title">
                            <span data-translate="user_recommend_rank_title">${commonUtil.escapeHtml(
                                t("user_recommend_rank_title", "Recommend rank"),
                            )}</span>
                            <span>(</span><span data-translate="entity_user">${commonUtil.escapeHtml(
                                t("entity_user", "User"),
                            )}</span><span>)</span>
                        </div>
                        <button type="button" class="btn circle-clear modal-window element-modal-close" data-role="${Roles.USER_RECOMMEND_MODAL_CLOSE}" aria-label="${commonUtil.escapeHtmlAttribute(
                            t("user_recommend_close", "Close"),
                        )}">&times;</button>
                    </div>

                    <div class="product-recommend-modal__body">
                        <div class="product-recommend-modal__list" data-role="${Roles.USER_RECOMMEND_MODAL_LIST}" aria-live="polite"></div>
                    </div>

                    <div class="modal-footer element-modal-footer product-recommend-modal__footer">
                        <button type="button" class="btn delete" data-action="${Actions.SELECT_CLEAR_ALL}" data-translate="remove_all">${commonUtil.escapeHtml(t("remove_all", "Remove All"))}</button>
                        <button type="button" class="btn clear" data-role="${Roles.USER_RECOMMEND_MODAL_CLEAR}" data-action="${Actions.SELECT_CLEAR}" data-translate="remove" disabled>${commonUtil.escapeHtml(t("remove", "Remove"))}</button>
                    </div>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML("beforeend", html);

        try {
            translator?.updateContent?.();
        } catch (_error) {
            return;
        }

        const overlayEl = this.getOverlay();
        const modalEl = this.getModal();
        const listRoot = this.getListRoot();
        const closeBtnEl = userRecommendForm.getCloseButton(document);

        if (!overlayEl || !modalEl || !listRoot) return;

        this.base = new ModalSearchBase({
            overlayEl,
            modalEl,
            filterInputEl: null,
            adapter: {
                render: (base: ModalSearchBaseLike) => {
                    this.renderList(base);
                },
                handleAction: (base: ModalSearchBaseLike, action: string, actionEl: Element, event: Event) => {
                    if (action === Actions.SELECT_RANK) {
                        event.preventDefault();
                        const btn = actionEl instanceof HTMLButtonElement ? actionEl : null;
                        if (btn?.disabled) return true;

                        const rankRaw = String(actionEl.getAttribute("data-rank") || "").trim();
                        if (!rankRaw) return true;

                        if (base.hasSelectedId(rankRaw)) {
                            return true;
                        }

                        base.toggleSelectedId(rankRaw);
                        base.render();

                        const rank = Number(rankRaw);
                        if (Number.isFinite(rank) && rank > 0) {
                            void this.onPickRank?.(Math.trunc(rank));
                        }
                        return true;
                    }

                    if (action === Actions.SELECT_CLEAR) {
                        event.preventDefault();
                        const btn = actionEl instanceof HTMLButtonElement ? actionEl : null;
                        if (btn?.disabled) return true;
                        void this.onUnset?.();
                        return true;
                    }

                    if (action === Actions.SELECT_CLEAR_ALL) {
                        event.preventDefault();
                        void this.onClearAll?.();
                        return true;
                    }

                    return false;
                },
            },
        }) as unknown as ModalSearchBaseLike;

        this.base.setSelectionMode("single");
        this.base.setCloseOnSelect(false);

        ModalSearchListener.setup({
            rootEl: document,
            scopeKey: "user-recommend-modal",
            overlayEl,
            modalEl,
            closeBtnEl: closeBtnEl instanceof HTMLElement ? closeBtnEl : null,
            filterInputEl: null,
            actionRootEls: [listRoot],
            base: this.base as unknown as ModalSearchBase,
        });
    }

    public renderBody(viewModel: RecommendViewModel): void {
        this.ensureOverlay();
        this.viewModel = viewModel;

        if (this.base) {
            const current = (Array.isArray(viewModel?.buttons) ? viewModel.buttons : []).find(
                (button) => !!button?.isCurrent,
            );
            const currentRank = current ? String(current.rank) : "";
            this.base.setSelectedIds(currentRank ? [currentRank] : []);
            this.base.render();
        }
    }

    public renderList(base: ModalSearchBaseLike): void {
        const listRoot = this.getListRoot();
        if (!listRoot) return;

        if (!this.viewModel) {
            const spinner = SpinnerRender.createInlineSpinner({ size: "normal" });
            listRoot.innerHTML = `
                <div class="product-recommend-modal__loading" aria-label="loading">
                    ${spinner.outerHTML}
                </div>
            `;
            return;
        }

        const buttons = Array.isArray(this.viewModel?.buttons) ? this.viewModel.buttons : [];
        const canUnset = !!this.viewModel?.canUnset;
        const recommendCaption = RecommendBadgeIconsUtils.getRecommendBadgeCaption("recommend", "Recommend");

        listRoot.innerHTML = buttons
            .map((button) => {
                const rank = Math.trunc(Number(button.rank));
                if (!Number.isFinite(rank) || rank <= 0) return "";

                const isSelected = base.hasSelectedId(String(rank));
                const selectedClass = isSelected ? " product-recommend-rank-option--selected" : "";
                const ariaPressed = isSelected ? "true" : "false";
                const disabledAttr = button.disabled ? "disabled" : "";

                return `
                    <button type="button" class="product-recommend-rank-option${selectedClass}" data-action="${Actions.SELECT_RANK}" aria-label="${commonUtil.escapeHtmlAttribute(
                        `${recommendCaption} ${String(rank)}`,
                    )}" data-rank="${commonUtil.escapeHtmlAttribute(
                        String(rank),
                    )}" aria-pressed="${ariaPressed}" ${disabledAttr}>
                        <span class="product-recommend-rank-option__icon is-recommended" data-recommend-rank="${commonUtil.escapeHtmlAttribute(
                            String(rank),
                        )}" aria-hidden="true">
                            ${RecommendBadgeIconsRender.recommendBadgeIconsTemplate}
                            <span class="recommend-badge__rank">${commonUtil.escapeHtml(String(rank))}</span>
                        </span>
                    </button>
                `;
            })
            .filter(Boolean)
            .join("");

        const modal = this.getModal();
        const removeBtn = modal ? userRecommendForm.getClearButton(modal) : null;
        if (removeBtn instanceof HTMLButtonElement) {
            removeBtn.disabled = !canUnset;
        }
    }

    public open(): void {
        this.ensureOverlay();
        this.base?.open();
    }

    public close(): void {
        this.base?.close();
    }

    public setBusy(busy: boolean): void {
        const modal = this.getModal();
        if (!modal) return;

        const buttons = userRecommendForm.getButtons(modal);
        buttons.forEach((button) => {
            if (busy) {
                if (!button.disabled) {
                    button.dataset.busyDisabled = "1";
                    button.disabled = true;
                }
                return;
            }

            if (button.dataset.busyDisabled === "1") {
                delete button.dataset.busyDisabled;
                button.disabled = false;
            }
        });
    }

    public setCrownState(el: HTMLElement, recommended: boolean): void {
        el.dataset.isRecommended = recommended ? "1" : "0";
        if (!recommended) {
            el.dataset.recommendRank = "";
        }
    }

    public setCrownRank(el: HTMLElement, rank: number | null): void {
        const normalized = rank === null ? null : Math.trunc(Number(rank));
        if (normalized === null || !Number.isFinite(normalized) || normalized <= 0) {
            el.dataset.recommendRank = "";
            const label = userRecommendForm.getRecommendBadgeRank(el);
            if (label instanceof HTMLElement) {
                label.remove();
            }
            return;
        }

        el.dataset.recommendRank = String(normalized);

        let label = userRecommendForm.getRecommendBadgeRank(el);
        const showRank = RecommendBadgeIconsUtils.shouldShowRecommendBadgeRank(el);
        if (!showRank) {
            if (label instanceof HTMLElement) {
                label.remove();
            }
            return;
        }
        if (!(label instanceof HTMLElement)) {
            label = document.createElement("span");
            label.className = "recommend-badge__rank";
            label.setAttribute("data-role", Roles.USER_RECOMMEND_BADGE_RANK);
            label.setAttribute("aria-hidden", "true");
            el.appendChild(label);
        }
        label.textContent = String(normalized);
    }
}
